/**
 * Time Role - Cast Pillar
 *
 * Normaliza input sujo para número, com detecção automática de unidade.
 *
 * @example
 * import { castTime, castTimeStrict, detectTimeUnit } from '@attrx/role-morphic/time/cast';
 *
 * castTime('5 min');                    // 300 (converted to seconds)
 * castTime('5 min', 'minute');          // 5 (kept as minutes)
 * castTimeStrict('100');                // 100 (no conversion)
 * detectTimeUnit('2 hours');            // 'hour'
 */

import { TIME_UNITS, TIME_ALIASES, type TimeUnit } from "./constants";
import { convertTime, TIME_BASE } from "./convert";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CAST
// =============================================================================

/**
 * Faz cast de input para número, com conversão opcional para unidade alvo
 *
 * @param input - Valor de entrada (number, string, etc)
 * @param targetVariant - Unidade alvo (default: second)
 * @returns Número ou null se não puder converter
 *
 * @example
 * castTime('5 min');            // 300 (seconds)
 * castTime('5 min', 'minute');  // 5
 * castTime('100');              // 100
 * castTime(100);                // 100
 * castTime('invalid');          // null
 */
export function castTime(
  input: unknown,
  targetVariant: TimeUnit = TIME_BASE,
): number | null {
  // Already a number
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  // String parsing
  if (typeof input === "string") {
    const parsed = parseTimeString(input);
    if (parsed === null) {
      return null;
    }

    // If unit was detected and differs from target, convert
    if (parsed.unit && parsed.unit !== targetVariant) {
      return convertTime(parsed.unit, targetVariant, parsed.value);
    }

    return parsed.value;
  }

  return null;
}

/**
 * Faz cast sem conversão (apenas parseia o número)
 *
 * @param input - Valor de entrada
 * @returns Número ou null
 *
 * @example
 * castTimeStrict('5 min');  // 5 (ignora a unidade)
 * castTimeStrict('100');    // 100
 */
export function castTimeStrict(input: unknown): number | null {
  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  if (typeof input === "string") {
    const parsed = parseTimeString(input);
    return parsed?.value ?? null;
  }

  return null;
}

/**
 * Versão safe de castTime que retorna Result
 */
export function tryCastTime(
  input: unknown,
  targetVariant: TimeUnit = TIME_BASE,
): Result<number> {
  const result = castTime(input, targetVariant);
  if (result === null) {
    return { ok: false, error: `Cannot cast "${String(input)}" to time` };
  }
  return { ok: true, value: result };
}

// =============================================================================
// DETECT UNIT
// =============================================================================

/**
 * Detecta a unidade de tempo de uma string
 *
 * @param input - String com valor e unidade
 * @returns Unidade detectada ou null
 *
 * @example
 * detectTimeUnit('5 min');     // 'minute'
 * detectTimeUnit('2 hours');   // 'hour'
 * detectTimeUnit('100');       // null
 */
export function detectTimeUnit(input: string): TimeUnit | null {
  const trimmed = input.trim().toLowerCase();

  // Extract unit part (after the number)
  const match = trimmed.match(/^[+-]?[\d.,\s]+\s*(.*)$/);
  if (!match || !match[1]) {
    return null;
  }

  const unitStr = match[1].trim();
  if (!unitStr) {
    return null;
  }

  // Check direct unit names
  if (unitStr in TIME_UNITS) {
    return unitStr as TimeUnit;
  }

  // Check aliases
  if (unitStr in TIME_ALIASES) {
    return TIME_ALIASES[unitStr] as TimeUnit;
  }

  // Check symbols (case-sensitive for some)
  for (const [unit, config] of Object.entries(TIME_UNITS)) {
    if (config.symbol.toLowerCase() === unitStr) {
      return unit as TimeUnit;
    }
  }

  return null;
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

interface ParsedTime {
  value: number;
  unit: TimeUnit | null;
}

/**
 * Parseia string de tempo extraindo valor e unidade
 */
function parseTimeString(input: string): ParsedTime | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  // Try to extract number and unit
  const match = trimmed.match(/^([+-]?[\d.,\s]+)\s*(.*)$/);
  if (!match) {
    return null;
  }

  const numStr = match[1].replace(/\s/g, "").replace(",", ".");
  const value = parseFloat(numStr);

  if (isNaN(value) || !Number.isFinite(value)) {
    return null;
  }

  const unitStr = match[2]?.trim().toLowerCase() || "";
  let unit: TimeUnit | null = null;

  if (unitStr) {
    // Check aliases first
    if (unitStr in TIME_ALIASES) {
      unit = TIME_ALIASES[unitStr] as TimeUnit;
    }
    // Check direct unit names
    else if (unitStr in TIME_UNITS) {
      unit = unitStr as TimeUnit;
    }
    // Check symbols
    else {
      for (const [u, config] of Object.entries(TIME_UNITS)) {
        if (config.symbol.toLowerCase() === unitStr) {
          unit = u as TimeUnit;
          break;
        }
      }
    }
  }

  return { value, unit };
}
